import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { 
  Settings, 
  ShoppingBag, 
  FileText, 
  TestTube, 
  Store,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Link,
  Eye,
  EyeOff,
  Upload,
  Download,
  Truck,
  Clock,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const Didi99FoodPage = () => {
  const [activeTab, setActiveTab] = useState('config');
  const [loading, setLoading] = useState(false);
  const [connection, setConnection] = useState(null);
  const [connectionId, setConnectionId] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    environment: 'TEST',
    baseUrl: 'https://openapi.99food.com',
    app_id: '',
    app_secret: '',
    app_shop_id: '',
    isEnabled: true
  });
  
  // Shop info
  const [shopInfo, setShopInfo] = useState(null);
  
  // Orders
  const [orders, setOrders] = useState([]);
  
  // Logs
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  
  // Menu
  const [menuUploadStatus, setMenuUploadStatus] = useState(null);
  const [importedMenu, setImportedMenu] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemActions, setItemActions] = useState({});
  
  // Delivery areas
  const [deliveryAreas, setDeliveryAreas] = useState([]);

  // Load connection on mount
  useEffect(() => {
    loadConnection();
  }, []);

  const loadConnection = async () => {
    try {
      setLoading(true);
      // Try to find existing connection for this store
      const response = await api.get('/integrations/didi99food/connections');
      if (response.data && response.data.length > 0) {
        const conn = response.data[0];
        setConnectionId(conn.connectionId || conn.id);
        await loadConnectionDetails(conn.connectionId || conn.id);
      }
    } catch (error) {
      // No connection exists yet
      console.log('No existing 99Food connection');
    } finally {
      setLoading(false);
    }
  };

  const loadConnectionDetails = async (connId) => {
    try {
      const response = await api.get(`/integrations/didi99food/connection/${connId}`);
      setConnection(response.data);
      setConnectionId(connId);
      setFormData({
        environment: response.data.environment || 'TEST',
        baseUrl: response.data.baseUrl || 'https://openapi.99food.com',
        app_id: response.data.credentialsMasked?.app_id || '',
        app_secret: '',
        app_shop_id: response.data.credentialsMasked?.app_shop_id || '',
        isEnabled: response.data.isEnabled !== false
      });
    } catch (error) {
      console.error('Error loading connection:', error);
    }
  };

  const handleSaveConnection = async () => {
    try {
      setLoading(true);
      
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (connectionId) {
        // Update
        await api.patch(`/integrations/didi99food/connection/${connectionId}`, {
          environment: formData.environment,
          baseUrl: formData.baseUrl,
          credentials: formData.app_secret ? {
            app_id: formData.app_id,
            app_secret: formData.app_secret,
            app_shop_id: formData.app_shop_id
          } : undefined,
          isEnabled: formData.isEnabled
        });
        toast.success('Configuração atualizada com sucesso');
      } else {
        // Create
        const response = await api.post('/integrations/didi99food/connection', {
          tenantId: user.tenant_id,
          storeId: user.store_id,
          environment: formData.environment,
          baseUrl: formData.baseUrl,
          credentials: {
            app_id: formData.app_id,
            app_secret: formData.app_secret,
            app_shop_id: formData.app_shop_id
          },
          isEnabled: formData.isEnabled
        });
        setConnectionId(response.data.connectionId);
        toast.success('Configuração salva com sucesso');
      }
      
      await loadConnectionDetails(connectionId || (await api.get('/integrations/didi99food/connections')).data[0]?.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!connectionId) {
      toast.error('Salve a configuração primeiro');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.post('/integrations/didi99food/auth/test', {
        connectionId
      });
      
      if (response.data.connected) {
        toast.success('Conexão bem-sucedida!');
        await loadConnectionDetails(connectionId);
      } else {
        toast.error(response.data.message || 'Falha na conexão');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || 'Erro ao testar conexão');
    } finally {
      setLoading(false);
    }
  };

  const handleGetAuthUrl = async () => {
    if (!connectionId) {
      toast.error('Salve a configuração primeiro');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.post('/integrations/didi99food/auth/authorization-url', {
        connectionId
      });
      
      if (response.data.url) {
        window.open(response.data.url, '_blank');
        toast.success('Link de autorização aberto em nova aba');
      }
    } catch (error) {
      toast.error('Erro ao obter URL de autorização');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshToken = async () => {
    if (!connectionId) return;
    
    try {
      setLoading(true);
      await api.post('/integrations/didi99food/auth/refresh-token', { connectionId });
      await api.post('/integrations/didi99food/auth/get-token', { connectionId });
      toast.success('Token atualizado com sucesso');
      await loadConnectionDetails(connectionId);
    } catch (error) {
      toast.error('Erro ao atualizar token');
    } finally {
      setLoading(false);
    }
  };

  const loadShopInfo = async () => {
    if (!connectionId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/integrations/didi99food/shop/detail?connectionId=${connectionId}`);
      setShopInfo(response.data.shop);
    } catch (error) {
      toast.error('Erro ao carregar informações da loja');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    if (!connectionId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/integrations/didi99food/logs?connectionId=${connectionId}&page=${logsPage}&pageSize=20`);
      setLogs(response.data.rows);
      setLogsTotal(response.data.total);
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveryAreas = async () => {
    if (!connectionId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/integrations/didi99food/delivery-area/list?connectionId=${connectionId}`);
      setDeliveryAreas(response.data.areas || []);
    } catch (error) {
      console.error('Error loading delivery areas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadMenu = async () => {
    if (!connectionId) return;
    
    try {
      setLoading(true);
      const response = await api.post('/integrations/didi99food/menu/upload-v3', {
        connectionId,
        mode: 'FULL',
        source: 'SYSTEM_PRODUCTS',
        options: {
          menuId: '1',
          defaultMenuName: 'Menu Principal',
          categoryIdPrefix: 'CAT_',
          itemIdStrategy: 'USE_PRODUCT_ID',
          includeModifiers: true
        }
      });
      
      setMenuUploadStatus(response.data);
      toast.success('Upload de menu iniciado');
    } catch (error) {
      toast.error('Erro ao fazer upload do menu');
    } finally {
      setLoading(false);
    }
  };

  const handleImportMenu = async () => {
    if (!connectionId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/integrations/didi99food/menu/import?connectionId=${connectionId}`);
      setImportedMenu(response.data);
      setSelectedItems(response.data.items.map(i => i.didi_item_id));
      
      // Inicializar ações como "create" para todos
      const actions = {};
      response.data.items.forEach(item => {
        actions[item.didi_item_id] = 'create';
      });
      setItemActions(actions);
      
      toast.success(`${response.data.total_items} itens importados do 99Food`);
    } catch (error) {
      toast.error('Erro ao importar menu do 99Food');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncMenu = async () => {
    if (!connectionId || selectedItems.length === 0) return;
    
    try {
      setLoading(true);
      
      const mappings = selectedItems.map(itemId => ({
        didi_item_id: itemId,
        action: itemActions[itemId] || 'create'
      }));
      
      const response = await api.post('/integrations/didi99food/menu/sync', {
        connectionId,
        mappings,
        create_missing_categories: true
      });
      
      const summary = response.data.summary;
      toast.success(`Sincronização concluída: ${summary.created} criados, ${summary.mapped} mapeados, ${summary.skipped} ignorados`);
      
      // Limpar seleção após sincronização
      setSelectedItems([]);
      setImportedMenu(null);
      
    } catch (error) {
      toast.error('Erro ao sincronizar menu');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'shop' && connectionId) {
      loadShopInfo();
    } else if (activeTab === 'logs' && connectionId) {
      loadLogs();
    } else if (activeTab === 'delivery' && connectionId) {
      loadDeliveryAreas();
    }
  }, [activeTab, connectionId, logsPage]);

  const getStatusBadge = () => {
    if (!connection) return <Badge variant="outline">Não Configurado</Badge>;
    
    const status = connection.token?.isExpired ? 'expired' : 
                   connection.token?.authTokenMasked ? 'authorized' : 'pending';
    
    switch (status) {
      case 'authorized':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Autenticado</Badge>;
      case 'expired':
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" />Token Expirado</Badge>;
      default:
        return <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Integração 99Food (DiDi)</h1>
          <p className="text-muted-foreground">Configure e gerencie a integração com o 99Food</p>
        </div>
        <div className="flex items-center gap-4">
          {getStatusBadge()}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full flex flex-wrap gap-1 h-auto p-1">
          <TabsTrigger value="config" className="flex items-center gap-2 flex-1 min-w-[120px]">
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Configuração</span>
          </TabsTrigger>
          <TabsTrigger value="shop" className="flex items-center gap-2 flex-1 min-w-[80px]">
            <Store className="w-4 h-4" />
            <span className="hidden sm:inline">Loja</span>
          </TabsTrigger>
          <TabsTrigger value="menu" className="flex items-center gap-2 flex-1 min-w-[80px]">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Menu</span>
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-2 flex-1 min-w-[80px]">
            <ShoppingBag className="w-4 h-4" />
            <span className="hidden sm:inline">Pedidos</span>
          </TabsTrigger>
          <TabsTrigger value="delivery" className="flex items-center gap-2 flex-1 min-w-[80px]">
            <Truck className="w-4 h-4" />
            <span className="hidden sm:inline">Entrega</span>
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2 flex-1 min-w-[80px]">
            <TestTube className="w-4 h-4" />
            <span className="hidden sm:inline">Logs</span>
          </TabsTrigger>
        </TabsList>

        {/* Config Tab */}
        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Credenciais da API
              </CardTitle>
              <CardDescription>
                Configure as credenciais de acesso à API do 99Food
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="app_id">App ID *</Label>
                  <Input
                    id="app_id"
                    data-testid="didi-app-id"
                    value={formData.app_id}
                    onChange={(e) => setFormData({...formData, app_id: e.target.value})}
                    placeholder="Ex: 3458764610605350993"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="app_shop_id">App Shop ID *</Label>
                  <Input
                    id="app_shop_id"
                    data-testid="didi-app-shop-id"
                    value={formData.app_shop_id}
                    onChange={(e) => setFormData({...formData, app_shop_id: e.target.value})}
                    placeholder="ID da sua loja no 99Food"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="app_secret">App Secret *</Label>
                <div className="relative">
                  <Input
                    id="app_secret"
                    data-testid="didi-app-secret"
                    type={showSecret ? 'text' : 'password'}
                    value={formData.app_secret}
                    onChange={(e) => setFormData({...formData, app_secret: e.target.value})}
                    placeholder={connection?.credentialsMasked?.app_secret || 'Digite o app_secret'}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowSecret(!showSecret)}
                  >
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {connection ? 'Deixe em branco para manter o secret atual' : 'Encontre no Portal do Parceiro DiDi'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="environment">Ambiente</Label>
                  <select
                    id="environment"
                    data-testid="didi-environment"
                    value={formData.environment}
                    onChange={(e) => setFormData({...formData, environment: e.target.value})}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="TEST">Teste (Sandbox)</option>
                    <option value="PROD">Produção</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="baseUrl">Base URL</Label>
                  <Input
                    id="baseUrl"
                    data-testid="didi-base-url"
                    value={formData.baseUrl}
                    onChange={(e) => setFormData({...formData, baseUrl: e.target.value})}
                    placeholder="https://openapi.99food.com"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isEnabled"
                  data-testid="didi-enabled"
                  checked={formData.isEnabled}
                  onCheckedChange={(checked) => setFormData({...formData, isEnabled: checked})}
                />
                <Label htmlFor="isEnabled">Integração Ativa</Label>
              </div>

              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={handleSaveConnection} 
                  disabled={loading}
                  data-testid="didi-save-btn"
                >
                  {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Salvar Configuração
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleTestConnection}
                  disabled={loading || !connectionId}
                  data-testid="didi-test-btn"
                >
                  <TestTube className="w-4 h-4 mr-2" />
                  Testar Conexão
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleGetAuthUrl}
                  disabled={loading || !connectionId}
                  data-testid="didi-auth-url-btn"
                >
                  <Link className="w-4 h-4 mr-2" />
                  Vincular Loja
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Token Info */}
          {connection && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Informações do Token</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <p className="font-medium">
                      {connection.token?.authTokenMasked ? (
                        connection.token?.isExpired ? 'Expirado' : 'Ativo'
                      ) : 'Não autenticado'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Expira em</p>
                    <p className="font-medium">
                      {connection.token?.tokenExpirationTime ? (
                        new Date(connection.token.tokenExpirationTime * 1000).toLocaleString('pt-BR')
                      ) : '-'}
                    </p>
                  </div>
                  <div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleRefreshToken}
                      disabled={loading || !connectionId}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Atualizar Token
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Shop Tab */}
        <TabsContent value="shop" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5" />
                Informações da Loja
              </CardTitle>
              <CardDescription>
                Detalhes e configurações da loja no 99Food
              </CardDescription>
            </CardHeader>
            <CardContent>
              {shopInfo ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Shop ID</p>
                    <p className="font-medium">{shopInfo.shop_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">App Shop ID</p>
                    <p className="font-medium">{shopInfo.app_shop_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Nome</p>
                    <p className="font-medium">{shopInfo.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge variant={shopInfo.biz_status === 1 ? 'default' : 'secondary'}>
                      {shopInfo.biz_status === 1 ? 'Online' : 'Offline'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Tempo de Preparo</p>
                    <p className="font-medium">{Math.round((shopInfo.promise_produce_time || 0) / 60)} min</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Auto Switch</p>
                    <p className="font-medium">{shopInfo.auto_switch === 1 ? 'Ativo' : 'Desativo'}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">
                    {connectionId ? 'Clique para carregar informações da loja' : 'Configure a conexão primeiro'}
                  </p>
                  <Button onClick={loadShopInfo} disabled={!connectionId || loading}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Carregar Informações
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Menu Tab */}
        <TabsContent value="menu" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Sincronização de Menu
              </CardTitle>
              <CardDescription>
                Importe itens do 99Food ou envie seu cardápio
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Importar do 99Food */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Importar Cardápio do 99Food
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Baixe os itens cadastrados no 99Food e vincule aos produtos do seu sistema.
                </p>
                <Button 
                  onClick={handleImportMenu} 
                  disabled={loading || !connectionId}
                  variant="outline"
                  data-testid="didi-import-menu-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {loading ? 'Carregando...' : 'Importar do 99Food'}
                </Button>
              </div>

              {/* Menu Importado */}
              {importedMenu && (
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h4 className="font-semibold">Menu: {importedMenu.menu_name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {importedMenu.total_categories} categorias • {importedMenu.total_items} itens
                      </p>
                    </div>
                    <Button 
                      onClick={handleSyncMenu} 
                      disabled={loading || selectedItems.length === 0}
                      data-testid="didi-sync-menu-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sincronizar Selecionados ({selectedItems.length})
                    </Button>
                  </div>

                  {/* Lista de Itens */}
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b">
                          <th className="p-2 text-left">
                            <input 
                              type="checkbox" 
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedItems(importedMenu.items.map(i => i.didi_item_id));
                                } else {
                                  setSelectedItems([]);
                                }
                              }}
                              checked={selectedItems.length === importedMenu.items.length}
                            />
                          </th>
                          <th className="p-2 text-left">Imagem</th>
                          <th className="p-2 text-left">Nome</th>
                          <th className="p-2 text-right">Preço</th>
                          <th className="p-2 text-center">Status</th>
                          <th className="p-2 text-left">Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importedMenu.items.map(item => (
                          <tr key={item.didi_item_id} className="border-b hover:bg-gray-50">
                            <td className="p-2">
                              <input 
                                type="checkbox" 
                                checked={selectedItems.includes(item.didi_item_id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedItems([...selectedItems, item.didi_item_id]);
                                  } else {
                                    setSelectedItems(selectedItems.filter(id => id !== item.didi_item_id));
                                  }
                                }}
                              />
                            </td>
                            <td className="p-2">
                              {item.image_url ? (
                                <img 
                                  src={item.image_url} 
                                  alt={item.name} 
                                  className="w-12 h-12 object-cover rounded"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                  <FileText className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                            </td>
                            <td className="p-2">
                              <p className="font-medium text-sm">{item.name}</p>
                              {item.description && (
                                <p className="text-xs text-muted-foreground truncate max-w-xs">{item.description}</p>
                              )}
                            </td>
                            <td className="p-2 text-right font-medium">
                              R$ {item.price.toFixed(2)}
                            </td>
                            <td className="p-2 text-center">
                              {item.is_available ? (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Ativo</span>
                              ) : (
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">Inativo</span>
                              )}
                            </td>
                            <td className="p-2">
                              <select 
                                className="text-sm border rounded p-1"
                                value={itemActions[item.didi_item_id] || 'create'}
                                onChange={(e) => setItemActions({
                                  ...itemActions, 
                                  [item.didi_item_id]: e.target.value
                                })}
                              >
                                <option value="create">Criar Produto</option>
                                <option value="skip">Ignorar</option>
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Enviar para 99Food */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Enviar Cardápio para 99Food
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Envie os produtos do seu sistema para o 99Food.
                </p>
                <Button 
                  onClick={handleUploadMenu} 
                  disabled={loading || !connectionId}
                  data-testid="didi-upload-menu-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Enviar Menu para 99Food
                </Button>

                {menuUploadStatus && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="font-medium">Status do Upload:</p>
                    <p className="text-sm text-muted-foreground">
                      Task ID: {menuUploadStatus.task?.taskID}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Status: {menuUploadStatus.task?.status === 0 ? 'Aguardando' : 'Processando'}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                Pedidos do 99Food
              </CardTitle>
              <CardDescription>
                Visualize e gerencie os pedidos recebidos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Os pedidos do 99Food aparecerão aqui quando forem recebidos.</p>
                <p className="text-sm mt-2">Configure o webhook ou polling para receber pedidos automaticamente.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Delivery Tab */}
        <TabsContent value="delivery" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Áreas de Entrega
              </CardTitle>
              <CardDescription>
                Configure as áreas e taxas de entrega
              </CardDescription>
            </CardHeader>
            <CardContent>
              {deliveryAreas.length > 0 ? (
                <div className="space-y-2">
                  {deliveryAreas.map((area, index) => (
                    <div key={area.area_id || index} className="p-4 border rounded-lg">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Tipo</p>
                          <p className="font-medium">{area.area_type === 0 ? 'Raio' : 'Polígono'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Raio</p>
                          <p className="font-medium">{area.radius || '-'} km</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Taxa</p>
                          <p className="font-medium">R$ {((area.price || 0) / 100).toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">ETA</p>
                          <p className="font-medium">{Math.round((area.avg_delivery_eta || 0) / 60)} min</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma área de entrega configurada</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={loadDeliveryAreas}
                    disabled={!connectionId || loading}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Carregar Áreas
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="w-5 h-5" />
                Logs de Integração
              </CardTitle>
              <CardDescription>
                Histórico de chamadas à API do 99Food
              </CardDescription>
            </CardHeader>
            <CardContent>
              {logs.length > 0 ? (
                <div className="space-y-2">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Data/Hora</th>
                          <th className="text-left p-2">Tipo</th>
                          <th className="text-left p-2">Método</th>
                          <th className="text-left p-2">Endpoint</th>
                          <th className="text-left p-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log, index) => (
                          <tr key={index} className="border-b">
                            <td className="p-2">{new Date(log.created_at).toLocaleString('pt-BR')}</td>
                            <td className="p-2">
                              <Badge variant="outline">{log.type}</Badge>
                            </td>
                            <td className="p-2">{log.method}</td>
                            <td className="p-2 max-w-xs truncate">{log.endpoint}</td>
                            <td className="p-2">
                              {log.status_code ? (
                                <Badge variant={log.status_code < 400 ? 'default' : 'destructive'}>
                                  {log.status_code}
                                </Badge>
                              ) : (
                                <Badge variant="secondary">-</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="flex justify-between items-center pt-4">
                    <p className="text-sm text-muted-foreground">
                      Total: {logsTotal} registros
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setLogsPage(p => Math.max(1, p - 1))}
                        disabled={logsPage === 1}
                      >
                        Anterior
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setLogsPage(p => p + 1)}
                        disabled={logs.length < 20}
                      >
                        Próximo
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <TestTube className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum log encontrado</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={loadLogs}
                    disabled={!connectionId || loading}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Carregar Logs
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Didi99FoodPage;
